import { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { BayWhizCopilot } from './BayWhizCopilot';
import { useAuthStore } from '../../hooks/useAuth';
import { isOperationsTierUser } from '../../utils/entitlements';

const LS_KEY = 'copilot_open';

function isStagingEnv(): boolean {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname;
    return h.includes('run.app') && h.startsWith('baywhiz-staging');
}

export function BayWhizCopilotWidget() {
    const { user } = useAuthStore();

    // Gate: only render on staging and for Operations-entitled users
    if (!isStagingEnv() || !isOperationsTierUser(user)) return null;

    const [open, setOpen] = useState<boolean>(() => {
        try {
            return localStorage.getItem(LS_KEY) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(LS_KEY, String(open));
        } catch { /* ignore */ }
    }, [open]);

    return (
        <>
            {/* Chat window – shown only when open */}
            {open && (
                <div
                    className={[
                        // Positioning
                        'fixed bottom-20 right-3 z-[9990]',
                        // Sizing: near-full width on mobile, fixed on md+
                        'w-[calc(100vw-24px)] max-w-sm',
                        // Height
                        'flex flex-col',
                        // Visual
                        'rounded-2xl shadow-2xl border border-border overflow-hidden',
                        // Animate in
                        'animate-in slide-in-from-bottom-4 fade-in duration-200',
                    ].join(' ')}
                    style={{ maxHeight: '480px' }}
                >
                    {/* Inner chat – takes remaining height */}
                    <div className="flex-1 overflow-hidden">
                        <BayWhizCopilot />
                    </div>
                </div>
            )}

            {/* FAB trigger – always visible */}
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? 'Close BayWhiz Copilot' : 'Open BayWhiz Copilot'}
                className={[
                    'fixed bottom-5 right-5 z-[9991]',
                    'w-14 h-14 rounded-full shadow-xl',
                    'bg-indigo-600 hover:bg-indigo-700 active:scale-95',
                    'flex items-center justify-center',
                    'transition-all duration-200',
                    !open ? 'animate-pulse' : '',
                ].join(' ')}
            >
                {open ? (
                    <X className="w-6 h-6 text-white" />
                ) : (
                    <>
                        <Sparkles className="w-6 h-6 text-white" />
                        {/* Badge */}
                        <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-[9px] font-black px-1.5 py-px rounded-full leading-none shadow-sm uppercase tracking-wide">
                            AI
                        </span>
                    </>
                )}
            </button>
        </>
    );
}
